<template>
  <div class="fixed z-10 inset-0 overflow-y-auto" id="contentPettyCashHistoryShow">
    <div class="flex items-center py-8 justify-center min-h-screen">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>
      <div class="bg-white rounded-lg overflow-hidden shadow-xl z-50 px-6 py-6 min-w-[92vw] md:min-w-[60vw]">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-medium text-gray-800">Detalle de caja chica</h2>
          <button
            @click="isActive"
            class="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg"
          >
            <svg class="w-5 h-5">
              <use href="../../assets/svg/icon.svg#cancel" />
            </svg>
          </button>
        </div>

        <LoadingForms v-if="!isLoading" />

        <div v-else class="space-y-6">
          <div class="grid md:grid-cols-4 gap-4">
            <div class="bg-blue-50 text-blue-600 rounded-lg p-4">
              <strong class="block text-xs uppercase">Monto inicial</strong>
              <span class="text-lg font-bold mt-2 block">{{ money() }} {{ format(dataForm.monto_inicial) }}</span>
            </div>
            <div class="bg-sky-50 text-sky-600 rounded-lg p-4">
              <strong class="block text-xs uppercase">Balance aumentado</strong>
              <span class="text-lg font-bold mt-2 block">{{ money() }} {{ format(summary.total_aumentado || 0) }}</span>
            </div>
            <div class="bg-red-50 text-red-500 rounded-lg p-4">
              <strong class="block text-xs uppercase">Total desembolsado</strong>
              <span class="text-lg font-bold mt-2 block">{{ money() }} {{ format(dataForm.total_desembolsado) }}</span>
            </div>
            <div class="bg-green-50 text-green-600 rounded-lg p-4">
              <strong class="block text-xs uppercase">Balance final</strong>
              <span class="text-lg font-bold mt-2 block">{{ money() }} {{ format(dataForm.balance_actual) }}</span>
            </div>
            <div class="rounded-lg p-4 border">
              <strong class="block text-xs uppercase text-gray-500">Estado</strong>
              <span class="text-sm font-bold mt-2 inline-block px-3 py-1 rounded-lg bg-green-50 text-green-600">
                ACTIVA
              </span>
            </div>
          </div>

          <div class="grid gap-4 text-sm">
            <div class="border rounded-lg p-4">
              <div><strong>Responsable apertura:</strong> {{ dataForm.usuario_apertura }}</div>
              <div class="mt-2"><strong>Fecha apertura:</strong> {{ formatDateTime(dataForm.fecha_apertura) }}</div>
              <div class="mt-2"><strong>Comentario apertura:</strong> {{ dataForm.comentario_apertura || '---' }}</div>
            </div>
          </div>

          <div class="rounded-lg overflow-hidden border">
            <div class="px-4 py-4 border-b font-bold text-gray-700">Movimientos registrados</div>
            <div class="overflow-x-auto">
              <table class="table-auto w-full">
                <TableHead :headers="header" />
                <tbody class="text-gray-500 text-left border-t-[1px]" v-if="movements.length > 0">
                  <tr
                    v-for="(movement, index) in movements"
                    :key="index"
                    class="odd:bg-white even:bg-slate-50 font-semibold"
                  >
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                      {{ movement.tipo_movimiento === 'AUMENTO' ? 'AUMENTO' : 'DESEMBOLSO' }}
                    </td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ movement.nombres }}</td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ movement.numero_referencia || '---' }}</td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ movement.beneficiario || '---' }}</td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ movement.departamento || '---' }}</td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">{{ movement.concepto }}</td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap" :class="{
                      'text-red-500': movement.tipo_movimiento === 'DESEMBOLSO',
                      'text-green-600': movement.tipo_movimiento === 'AUMENTO',
                    }">{{ money() }} {{ format(movement.monto) }}</td>
                    <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">{{ formatDateTime(movement.fecha_de_desembolso) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="movements.length === 0" class="px-4 py-6 text-center text-gray-400 font-semibold">
              Esta caja chica no tiene desembolsos registrados.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import axios from "@/api";
import { errorActions } from "@/alerts";
import { observeElement } from "@/observer";
import LoadingForms from "@/components/Loadings/LoadingFormsOneColumn.vue";
import TableHead from "@/components/TablesComponents/TableHead";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";

export default {
  name: "ShowPettyCashHistory",
  components: {
    LoadingForms,
    TableHead,
  },
  props: {
    activeModal: {
      type: Function,
      required: true,
    },
    id: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const isLoading = ref(false);
    const dataForm = ref({});
    const movements = ref([]);
    const summary = ref({});
    const header = [
      [
        { title: "tipo" },
        { title: "responsable" },
        { title: "referencia" },
        { title: "beneficiario" },
        { title: "departamento" },
        { title: "concepto" },
        { title: "monto" },
        { title: "fecha" },
      ],
    ];

    const isActive = () => {
      props.activeModal(true);
    };

    onMounted(async () => {
      observeElement("#contentPettyCashHistoryShow");
      const response = await axios
        .get(`caja-chica/${props.id}`)
        .catch((error) => errorActions(error));

      if (!response) {
        return;
      }

      const data = await response.data;
      dataForm.value = data.data;
      movements.value = data.movements;
      summary.value = data.summary || {};
      isLoading.value = true;
    });

    return {
      isLoading,
      dataForm,
      movements,
      summary,
      header,
      isActive,
      money,
      format,
      formatDateTime,
    };
  },
};
</script>